import type { MouseEvent } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import clsx from 'clsx'
import { isBefore } from 'date-fns'
import { useUnit } from 'effector-react'
import emojiRegex from 'emoji-regex'
import { Reorder } from 'framer-motion'
import type { GIFObject } from 'giphy-api'
import debounce from 'lodash/debounce'
import { AudioVisualizer } from 'react-audio-visualize'

import CloseIcon from '@/assets/icons/icon-close.svg'
import EmojiIcon from '@/assets/icons/icon-emoji.svg'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import type { CheckedState } from '@/components/ui/form/form-checkbox.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import { getUserMessageEditorModels } from '@/models/chattingUI/messageEditor/messageEditor.ts'
import {
  $fanLanguage,
  translateTextsFx,
} from '@/models/chattingUI/messages/translations.ts'
import { startTypingDebounced } from '@/models/chattingUI/pusher.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import {
  $focusedScriptId,
  $isScriptsOpen,
  resetFocusedScriptId,
  resetScriptsFilter,
  setScriptsFilter,
  setScriptsOpen,
} from '@/models/scripts/chatting.ts'
import { useSendMassMessage } from '@/modules/chatting/hooks/use-mass-message-send'
import { useAudioFile } from '@/modules/chatting/hooks/useAudioFile.ts'
import ActionAITranslationIcon from '@/modules/chatting/icons/action-ai-translation.svg'
import ActionPriceTag from '@/modules/chatting/icons/action-price-tag.svg'
import ActionScriptIcon from '@/modules/chatting/icons/action-scripts.svg'
import { useChatting } from '@/modules/chatting/state/chatting-provider.tsx'
import type {
  MediaAttachment,
  MessageSendRequest,
  UploadedFileDetails,
} from '@/modules/chatting/types'
import { ChatTags } from '@/modules/chatting/ui/chat-window/chat-tags.tsx'
import { ReplyToMessageContainer } from '@/modules/chatting/ui/chat-window/reply-to-message-container.tsx'
import { AudioPlayPauseButton } from '@/modules/chatting/ui/components/AudioPlayPauseButton'
import { Dropzone } from '@/modules/chatting/ui/components/dropzone/Dropzone.tsx'
import { DropzoneIndicationWrapper } from '@/modules/chatting/ui/components/dropzone/DropzoneIndicationWrapper.tsx'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import {
  checkSendDisabled,
  isUploadedFile,
  secondsToTime,
} from '@/modules/chatting/ui/utils'
import { BlacklistedWordDetectionError } from '@/modules/chatting/utils/blacklistedWords.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { useSentScriptIdsByFanIdQuery } from '@/modules/scripts/hooks/useSentScriptIdsByFanIdQuery'
import type { ScriptNode } from '@/modules/scripts/types'
import type {
  StreamableVaultMedia,
  UploadedMediaAction,
} from '@/modules/vault/types.ts'
import { UploadedMediaPreview } from '@/modules/vault/ui/UploadedMediaPreview.tsx'
import { VaultMediaItem } from '@/modules/vault/ui/media/vault-media-item'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'
import { classNames } from '@/utils/helpers.ts'
import { validateMessagePrice } from '@/utils/message-price'
import {
  isDocumentType,
  isGuestType,
  isUserType,
} from '@/utils/releaseForms.ts'

import { ChattingMessageEditorActions } from '../components/ChattingMessageEditorActions/ChattingMessageEditorActions.tsx'
import { ChatGifMedia } from './chat-gif-media'
import { ChatScheduleMessageIndicator } from './chat-schedule-message-indicator'
import {
  ACCEPTABLE_FORMARS,
  MESSAGE_PRICE_RANGE,
  NON_EXISTENT_USER_ID,
} from './constants'
import {
  EditorEventHandlerPluginKey,
  EditorEventHandlerPluginOrder,
} from './message-editor/consts.ts'
import { MessageEditor } from './message-editor/message-editor'
import { MessageEditorProvider } from './message-editor/message-editor-provider'
import { MessageFormatter } from './message-editor/message-formatter'
import { useEditor } from './message-editor/use-editor'
import { ChatScriptTag } from './scripts/chat-script-tag'
import { ChatScripts } from './scripts/chat-scripts'
import { TranslationHint } from './translation/TranslationHint.tsx'
import { TranslationTutorial } from './translation/tutorial-dialog'
import { PersistingChatMessage } from './types.ts'

interface Props {
  userId?: number
  onSendMessage: (message: Partial<MessageSendRequest>) => Promise<unknown>
  canBeSent?: boolean
  sendAsPriorityAllowed?: boolean
  isChatMessageEditor?: boolean
}

export const ChattingMessageEditorComponent = ({
  userId = NON_EXISTENT_USER_ID,
  onSendMessage,
  canBeSent,
  sendAsPriorityAllowed,
  isChatMessageEditor = true,
}: Props) => {
  const creatorId = useUnit($creatorId)
  const fanId = useUnit($fanId)
  const { replyToMessage, setReplyToMessage, isScriptAutoSuggestionEnabled } =
    useChatting()
  const { refetch: invalidateSentScriptIds } =
    useSentScriptIdsByFanIdQuery(userId)
  const [selectedScript, setSelectedScript] = useState<ScriptNode>()
  const [loadingScriptMediaCount, setLoadingScriptMediaCount] = useState(0)

  const {
    editor,
    handleSetContent,
    handleReset,
    handleInsertContent,
    registerEnterHandlerPlugin,
    text,
    message,
    handleFocus,
  } = useEditor()
  const fanLanguage = useUnit($fanLanguage)
  const models = getUserMessageEditorModels(userId)

  const {
    medias: {
      events: {
        uploadMediaFx,
        cancelUploading,
        setMedias,
        removeMedia,
        setIsPreviewMedia,
      },
      stores: { $medias, $uploadingMedias },
    },
    cloneMedia: {
      stores: { $cloneMediaJobsInProgress },
      events: { resetCloneMediaJobs },
    },
    tags: {
      events: { setTags, removeTag },
      stores: { $tags },
    },
  } = models
  const tags = useUnit($tags)
  const medias = useUnit($medias)
  const uploadingMedias = useUnit($uploadingMedias)
  const cloneMediaJobsInProgress = useUnit($cloneMediaJobsInProgress)

  const toast = useToast()

  const isScriptsOpen = useUnit($isScriptsOpen)
  const scriptsTriggerRef = useRef<HTMLButtonElement>(null)
  const isTranslating = useUnit(translateTextsFx.pending)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>()
  const focusedScriptId = useUnit($focusedScriptId)
  const [price, setPrice] = useState<number>()
  const [isFormattingOpen, setIsFormattingOpen] = useState(false)
  const [attachedAudio, setAttachedAudio] = useState<MediaAttachment>()
  const [giphyId, setGiphyId] = useState<GIFObject['id'] | null>(null)
  const {
    emojiTaskbar,
    emojiTaskbarEnabled,
    messageMinPrice,
    messageMaxPrice,
    showScripts,
    aiTranslationEnabled,
  } = useSettingsContext()
  const [sendAsPriority, setSendAsPriority] = useState<CheckedState>(false)
  const handleSendAsPriorityChange = useCallback(
    (value: CheckedState) => {
      if (value) {
        setScheduledDate(undefined)
      }

      setSendAsPriority(value)
    },
    [setScheduledDate, setSendAsPriority],
  )

  useEffect(() => {
    if (scheduledDate && sendAsPriority === true) {
      setSendAsPriority(false)
    }
  }, [scheduledDate, sendAsPriority, setSendAsPriority])

  const { handlePause, handlePlay, isPlaying, currentTime } = useAudioFile(
    attachedAudio?.url,
  )
  const [isMessageSending, setIsMessageSending] = useState(false)

  const handleCancelGifSelection = useCallback(() => {
    setGiphyId(null)
  }, [])

  const handleClickAITranslationIcon = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.TRANSLATION_CONFIG_DIALOG,
    })
  }, [])

  const handleResetEditor = useCallback(
    (resetLocalData = true) => {
      if (resetLocalData) {
        handleReset()
        setReplyToMessage(undefined)
        setPrice(undefined)
        setMedias([])
        setTags([])
        setAttachedAudio(undefined)
        setScheduledDate(undefined)
        resetCloneMediaJobs()
        setSelectedScript(undefined)
        handleCancelGifSelection()
      }
      setIsMessageSending(false)
      setScriptsOpen(false)
    },
    [
      handleReset,
      setReplyToMessage,
      handleCancelGifSelection,
      setMedias,
      resetCloneMediaJobs,
      setTags,
    ],
  )

  const setSessionStorage = useCallback(
    (values: PersistingChatMessage = {}) => {
      if (
        editor?.isActive &&
        editor.isInitialized &&
        userId !== NON_EXISTENT_USER_ID
      ) {
        const {
          medias = [],
          tags = [],
          price = 0,
          text = '',
          replyToMessage = undefined,
          attachedAudio = undefined,
          selectedScript = undefined,
          giphyId = null,
          userId,
        } = values
        const sessionData = sessionStorage.getItem(LocalStorageKeys.FAN_MESSAGE)
        const fanMessageDataObjectCopy = sessionData
          ? JSON.parse(sessionData)
          : {}

        fanMessageDataObjectCopy[`${userId}`] = {
          medias,
          tags,
          price,
          text,
          replyToMessage,
          attachedAudio,
          selectedScript,
          giphyId,
          userId,
        }

        sessionStorage.setItem(
          LocalStorageKeys.FAN_MESSAGE,
          JSON.stringify(fanMessageDataObjectCopy),
        )
      }
    },
    [editor?.isActive, editor?.isInitialized, userId],
  )

  const debouncedSetScriptsFilter = useMemo(
    () => debounce((text: string) => setScriptsFilter(text), 200),
    [],
  )

  const closeScriptsSuggestions = useCallback(() => {
    setScriptsOpen(false)
    resetFocusedScriptId()
    resetScriptsFilter()
  }, [])

  const toggleScriptsSuggestions = useCallback(() => {
    if (isScriptsOpen) {
      closeScriptsSuggestions()
    } else {
      setScriptsOpen(true)
    }
  }, [closeScriptsSuggestions, isScriptsOpen])

  useEffect(() => {
    if (!isScriptAutoSuggestionEnabled) {
      closeScriptsSuggestions()
    }
  }, [isScriptAutoSuggestionEnabled, closeScriptsSuggestions])

  const { sendMassMessage } = useSendMassMessage({
    onSuccess: () => {
      toast.success('Message scheduled successfully')
    },
  })

  const restoreEditor = useCallback(
    (persistingChatMessage: PersistingChatMessage) => {
      const {
        medias = [],
        tags = [],
        price,
        text = '',
        replyToMessage,
        attachedAudio,
        selectedScript,
        giphyId = null,
      } = persistingChatMessage

      setMedias(medias)
      handleSetContent(text, true)
      setTags(tags)
      setPrice(price)
      setSelectedScript(selectedScript)
      setReplyToMessage(replyToMessage)
      setAttachedAudio(attachedAudio)
      setGiphyId(giphyId)
      handleResetEditor(false)
      resetCloneMediaJobs()
      setIsFormattingOpen(false)

      setTimeout(() => {
        handleFocus()
      }, 1)
    },
    [
      setMedias,
      handleSetContent,
      setReplyToMessage,
      handleResetEditor,
      handleFocus,
      resetCloneMediaJobs,
      setTags,
    ],
  )

  useEffect(
    () => {
      const sessionData = sessionStorage.getItem(LocalStorageKeys.FAN_MESSAGE)
      const fanMessageDataObjectCopy =
        sessionData && userId !== NON_EXISTENT_USER_ID
          ? JSON.parse(sessionData)
          : {}

      restoreEditor(fanMessageDataObjectCopy[`${userId}`] ?? {})
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId],
  )

  const persistingDetails = useMemo<PersistingChatMessage>(() => {
    return {
      medias,
      tags,
      price,
      text: message,
      replyToMessage,
      attachedAudio,
      selectedScript,
      giphyId,
      userId,
    }
  }, [
    medias,
    price,
    replyToMessage,
    tags,
    message,
    attachedAudio,
    selectedScript,
    giphyId,
    userId,
  ])

  useEffect(() => {
    setSessionStorage(persistingDetails)
  }, [persistingDetails, setSessionStorage])

  const handleFixedEmojiClick = useCallback(
    (str: RegExpExecArray) => {
      if (editor?.isInitialized) {
        handleInsertContent(String(str))
      }
    },
    [editor?.isInitialized, handleInsertContent],
  )

  const {
    isDisabled: isSendDisabled,
    reason: sendDisabledReason,
    isLoading,
  } = checkSendDisabled({
    canBeSent,
    uploadingMedias,
    text,
    medias,
    attachedAudio,
    giphyId,
    isTranslating,
    isLoadingScriptMedias: !!loadingScriptMediaCount,
    cloneMediaJobsInProgress,
    price,
  })

  const handleSend = useCallback(
    async (message: string) => {
      if (isSendDisabled) {
        return
      }

      if (price && !medias.length && !attachedAudio) {
        toast.warning('Please remove the price or add media')

        return
      }

      const { MIN: min, MAX: max } = MESSAGE_PRICE_RANGE

      if (!validateMessagePrice(price, min, max)) {
        toast.warning(`Price must be between $${min} and $${max}`)

        return
      }

      if (scheduledDate && isBefore(scheduledDate, new Date())) {
        toast.warning('Scheduled date must be in the future')

        return
      }

      setIsMessageSending(true)

      const mediaFiles: (UploadedFileDetails | number)[] = medias.map((f) =>
        isUploadedFile(f) ? f : f.id,
      )

      if (attachedAudio) {
        mediaFiles.push(attachedAudio.media)
      }

      const previews = medias
        .filter((m) => m.isPreview)
        .map((item) => {
          if (isUploadedFile(item)) {
            return item.processId
          } else {
            return item.id
          }
        })

      const messageDetails: Partial<MessageSendRequest> = {
        text: message,
        price,
        mediaFiles,
        previews,
        giphyId,
        rfGuest: tags
          .filter(({ type }) => type && isGuestType(type))
          .map(({ id }) => id),
        rfPartner: tags
          .filter(({ type }) => type && isDocumentType(type))
          .map(({ id }) => id),
        rfTag: tags
          .filter(({ type }) => type && isUserType(type))
          .map(({ id }) => id),
        scriptId: selectedScript?.id,
        priorityMassMessage: !!sendAsPriority,
      }

      trackFeatureUsage(TrackedFeatures.CUIMessaging, creatorId)

      if (scheduledDate && userId !== NON_EXISTENT_USER_ID) {
        try {
          await sendMassMessage({
            ...messageDetails,
            scheduledDate: scheduledDate.toISOString(),
            totalRecepientsCount: 1,
            userIds: [userId],
          })
          handleResetEditor()
          setSessionStorage()
        } finally {
          setIsMessageSending(false)
        }

        return
      }

      try {
        // for chat messages
        if (userId !== NON_EXISTENT_USER_ID) {
          handleResetEditor()
        }
        await onSendMessage({
          ...messageDetails,
          ...(replyToMessage && { replyToMessageId: replyToMessage.id }),
          ...(scheduledDate && { scheduledDate: scheduledDate.toISOString() }),
        })
        // for mass messages
        if (userId === NON_EXISTENT_USER_ID) {
          handleResetEditor()
        }
        if (selectedScript) {
          invalidateSentScriptIds()
        }

        setSessionStorage()

        setTimeout(() => handleFocus(), 0)
      } catch (error) {
        if (userId && error instanceof BlacklistedWordDetectionError) {
          restoreEditor(persistingDetails)
        }
      } finally {
        setIsMessageSending(false)
      }
    },
    [
      isSendDisabled,
      price,
      medias,
      attachedAudio,
      scheduledDate,
      giphyId,
      tags,
      selectedScript,
      sendAsPriority,
      userId,
      toast,
      sendMassMessage,
      handleResetEditor,
      setSessionStorage,
      onSendMessage,
      replyToMessage,
      invalidateSentScriptIds,
      handleFocus,
      persistingDetails,
      restoreEditor,
      creatorId,
    ],
  )

  useEffect(
    () => {
      registerEnterHandlerPlugin({
        key: EditorEventHandlerPluginKey.MAIN,
        order: EditorEventHandlerPluginOrder.MAIN,
        enterHandler: () => {
          if (!isSendDisabled && !focusedScriptId) {
            handleSend(message)
          }
        },
        keydownHandler: (event) => {
          if (event.code !== 'Escape' || !fanLanguage) {
            return
          }
          if (text.trim().length > 0 && showScripts) {
            setScriptsOpen(true)
          }
        },
        inputHandler: () => {
          startTypingDebounced(fanId)

          if (isChatMessageEditor && isScriptAutoSuggestionEnabled) {
            debouncedSetScriptsFilter(text?.trim() ?? '')
          }
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editor,
      focusedScriptId,
      handleSend,
      isScriptAutoSuggestionEnabled,
      isChatMessageEditor,
      isSendDisabled,
      registerEnterHandlerPlugin,
      text,
      message,
      fanLanguage,
      isTranslating,
      fanId,
      debouncedSetScriptsFilter,
    ],
  )

  const handlePriceDialogOpen = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CHAT_PRICE,
      onSave: setPrice,
      defaultPrice: price,
      messageMinPrice: messageMinPrice,
      messageMaxPrice: messageMaxPrice,
      userId,
    })
  }, [messageMaxPrice, messageMinPrice, price, userId])

  const handlePriceCancel = useCallback((event: MouseEvent<SVGElement>) => {
    event.stopPropagation()
    setPrice(undefined)
  }, [])

  const handleSubmitButton = useCallback(
    () => handleSend(message),
    [handleSend, message],
  )

  const emojiItems = useMemo(() => {
    const emojis = emojiTaskbar!.matchAll(emojiRegex())
    const res = []

    for (const emoji of emojis) {
      res.push(emoji)
    }

    return res
  }, [emojiTaskbar])

  const handleFileChange = useCallback(
    async (files: File[] | FileList) => {
      for (const file of files) {
        try {
          await uploadMediaFx(file)
        } finally {
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }
      }
    },
    [uploadMediaFx],
  )

  const openAudioDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ADD_AUDIO,
      onAddAudio: setAttachedAudio,
    })
  }, [])

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length !== 1) return

      // if file is audio
      if (acceptedFiles[0].type.startsWith('audio/')) {
        openDialog({
          type: DIALOG_TYPE.UPLOAD_AUDIO,
          audio: acceptedFiles[0],
          onUpload: openAudioDialog,
        })
      } else {
        // if file is not audio
        handleFileChange(acceptedFiles)
      }
    },
    [handleFileChange, openAudioDialog],
  )

  const handleScriptClick = useCallback(
    (script: ScriptNode) => {
      setSelectedScript(script)
      handleFocus()
    },
    [handleFocus],
  )

  const handleSelectGif = (gifId: GIFObject['id']) => {
    setGiphyId(gifId)

    setTimeout(() => {
      handleFocus()
    }, 100)
  }

  const additionalMediaActions = useCallback(
    (media: StreamableVaultMedia | UploadedFileDetails, index: number) => {
      const actions: UploadedMediaAction[] = []

      if (!!price && media.isPreview) {
        actions.push({
          text: 'Remove free preview',
          onClick: (event) => {
            event.stopPropagation()
            setIsPreviewMedia({ index, isPreview: false })
          },
        })
      }

      if (!!price && !media.isPreview) {
        actions.push({
          text: 'Send as free preview',
          onClick: (event) => {
            event.stopPropagation()
            setIsPreviewMedia({ index, isPreview: true })
          },
        })
      }

      return actions
    },
    [setIsPreviewMedia, price],
  )
  const handleMediaPreview = useCallback(
    (mediaId: number) => {
      openDialog({
        type: DIALOG_TYPE.VAULT_MEDIA_PREVIEW,
        // @ts-ignore
        mediaList: medias.map(({ id }) => id).filter(Boolean),
        mediaId,
      })
    },
    [medias],
  )

  const handleClickFormat = useCallback(
    () => setIsFormattingOpen((val) => !val),
    [],
  )

  return (
    <>
      <Dropzone
        onDrop={handleFileDrop}
        noClick
        accept={ACCEPTABLE_FORMARS}
      >
        {({ getRootProps }) => (
          <div
            {...getRootProps()}
            tabIndex={-1}
            className={classNames(
              'bg-card relative flex flex-col gap-3 rounded-lg border px-6 py-4 transition',
              {
                'border-primary-500': !!price,
              },
            )}
          >
            {replyToMessage && (
              <div className="mb-1.5">
                <ReplyToMessageContainer />
              </div>
            )}
            {!!scheduledDate && (
              <ChatScheduleMessageIndicator
                scheduledDate={scheduledDate}
                onClearScheduledMessage={() => setScheduledDate(undefined)}
              />
            )}
            <div className="flex w-full justify-between gap-3">
              {emojiTaskbarEnabled ? (
                <div className="bg-secondary-50 dark:bg-secondary-600 flex w-max gap-3 overflow-hidden rounded-lg px-1 pt-1">
                  <div className="big-scrollbar flex max-w-[600px] items-center gap-3 overflow-auto pb-1">
                    {emojiItems.map((i, index) => (
                      <div
                        onClick={() => handleFixedEmojiClick(i)}
                        className="hover:border-primary-500 flex h-6 w-6 cursor-pointer select-none items-center justify-center rounded-full border border-transparent p-1 transition"
                        key={index}
                      >
                        {i[0]}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex w-max gap-3">
                  <Tooltip content="The Emoji taskbar is turned off. You can enable it in Creator Settings → Display Settings">
                    <EmojiIcon className="text-secondary-300 h-5 w-5" />
                  </Tooltip>
                </div>
              )}
              <div className="text-secondary-300 flex items-center gap-2">
                {!!price && (
                  <div
                    onClick={handlePriceDialogOpen}
                    className="bg-primary-500 flex cursor-pointer items-center rounded px-2.5 py-2"
                  >
                    <ActionPriceTag className="h-5 w-5 text-white" />
                    <span className="mx-2 text-sm font-medium text-white">
                      Price to View: ${price}
                    </span>
                    <CloseIcon
                      className="text-secondary-100 cursor-pointer"
                      onClick={handlePriceCancel}
                    />
                  </div>
                )}
                {aiTranslationEnabled && !isChatMessageEditor && (
                  <IconButton
                    tooltip="AI Language Translator"
                    icon={ActionAITranslationIcon}
                    onClick={handleClickAITranslationIcon}
                  />
                )}
                {isChatMessageEditor &&
                  (showScripts ? (
                    <IconButton
                      tooltip="Scripts"
                      testId="scripts-launcher"
                      icon={ActionScriptIcon}
                      onClick={toggleScriptsSuggestions}
                      ref={scriptsTriggerRef}
                    />
                  ) : (
                    <Tooltip content="Scripts are disabled. You can enable it in Creator Settings → Visibility">
                      <ActionScriptIcon />
                    </Tooltip>
                  ))}
              </div>
            </div>
            <div className="absolute bottom-[calc(100%+8px)] left-0 right-0 w-full space-y-4 z-[2]">
              {isChatMessageEditor && isScriptsOpen && (
                <ChatScripts
                  setMedias={setMedias}
                  setText={(value) => {
                    handleSetContent(value, true)
                  }}
                  setPrice={setPrice}
                  triggerRef={scriptsTriggerRef}
                  onScriptClick={handleScriptClick}
                  setLoadingMediaCount={setLoadingScriptMediaCount}
                />
              )}
              {!!fanLanguage && (
                <TranslationHint
                  onSend={handleSend}
                  isSending={isMessageSending}
                  isSendDisabled={isSendDisabled}
                />
              )}
            </div>
            <div
              className={clsx(
                'flex flex-col gap-3 overflow-y-auto',
                userId !== NON_EXISTENT_USER_ID
                  ? 'max-h-[40vh]'
                  : 'max-h-[280px]',
              )}
            >
              {attachedAudio && (
                <div className="bg-primary-50 dark:bg-primary-900 flex w-full max-w-[289px] items-center justify-between rounded-lg px-3 py-2.5">
                  <div className="bg-title h-8 w-8 rounded-full">
                    <AudioPlayPauseButton
                      isPlaying={isPlaying}
                      onPlay={handlePlay}
                      onPause={handlePause}
                      className="h-full w-full"
                      iconClassName="w-full h-full fill-white dark:fill-secondary-700"
                    />
                  </div>
                  <AudioVisualizer
                    barPlayedColor="#1E84E3"
                    currentTime={currentTime}
                    blob={attachedAudio.blob}
                    width={130}
                    height={37}
                    barWidth={2}
                    barColor="#898C9A"
                    gap={4}
                  />
                  <span className="text-sm">
                    {secondsToTime(Math.floor(attachedAudio.duration))}
                  </span>
                  <IconButton
                    icon={CloseIcon}
                    onClick={() => setAttachedAudio(undefined)}
                  />
                </div>
              )}
              {!!giphyId && (
                <div>
                  <ChatGifMedia
                    giphyId={giphyId}
                    className="h-[120px] w-[120px]"
                    onRemove={handleCancelGifSelection}
                  />
                </div>
              )}
              {!!loadingScriptMediaCount && (
                <ul className="flex flex-1 flex-nowrap items-end justify-start overflow-x-scroll">
                  {[...Array(loadingScriptMediaCount)].map((_, index) => (
                    <li
                      className="relative mb-2 mr-2 h-[209px] w-[153px] shrink-0 rounded-md"
                      key={index}
                    >
                      <Skeleton className="h-full w-full dark:bg-primary-900" />
                    </li>
                  ))}
                </ul>
              )}
              {(!!medias.length ||
                !!uploadingMedias.length ||
                !!cloneMediaJobsInProgress.length) && (
                <div>
                  <Reorder.Group
                    className="flex flex-1 flex-nowrap items-end justify-start overflow-x-scroll"
                    axis="x"
                    onReorder={setMedias}
                    values={medias}
                  >
                    {medias.map((m, index) => (
                      <Reorder.Item
                        className="mb-2 mr-2 h-[209px] w-[153px] shrink-0"
                        key={`${isUploadedFile(m) ? m.processId : m.id}-${userId}`}
                        value={m}
                      >
                        <div
                          className={classNames(
                            'group relative h-full w-full rounded-md overflow-hidden',
                            {
                              'border border-primary-500': !m,
                            },
                          )}
                        >
                          {isUploadedFile(m) ? (
                            <UploadedMediaPreview
                              onClear={removeMedia}
                              item={m}
                            />
                          ) : (
                            <VaultMediaItem
                              item={m}
                              onClear={removeMedia}
                              additionalActions={additionalMediaActions(
                                m,
                                index,
                              )}
                              className="bg-secondary-50"
                              onMediaPreview={handleMediaPreview}
                            />
                          )}
                        </div>
                      </Reorder.Item>
                    ))}
                    {uploadingMedias?.map((item) => (
                      <li
                        className="relative mb-2 mr-2 h-[209px] w-[153px] shrink-0 rounded-md"
                        key={item.id}
                      >
                        <Skeleton className="h-full w-full dark:bg-primary-900" />
                        <IconButton
                          icon={CloseIcon}
                          onClick={() => cancelUploading(item.id)}
                          className="bg-secondary-100 dark:bg-secondary-600 absolute right-2 top-2 z-10 rounded-full p-1"
                        />
                        <div className="bg-muted absolute left-0 top-0 flex h-full w-full items-center justify-center text-lg font-bold uppercase">
                          {item.progress}%
                        </div>
                      </li>
                    ))}
                    {cloneMediaJobsInProgress?.map((job) => (
                      <li
                        className="relative mb-2 mr-2 h-[209px] w-[153px] shrink-0 rounded-md"
                        key={job.id}
                      >
                        <Skeleton className="h-full w-full" />
                        <div className="bg-muted dark:bg-primary-900 absolute left-0 top-0 flex h-full w-full items-center justify-center text-lg font-bold uppercase">
                          {job.progress || 0}%
                        </div>
                      </li>
                    ))}
                  </Reorder.Group>
                </div>
              )}
              <DropzoneIndicationWrapper>
                {editor?.isInitialized && <MessageEditor />}
                <MessageFormatter
                  isVisible={isFormattingOpen}
                  onClose={() => setIsFormattingOpen(false)}
                />
              </DropzoneIndicationWrapper>
            </div>
            {tags.length > 0 && (
              <ChatTags
                onRemove={removeTag}
                tags={tags}
              />
            )}
            {selectedScript && (
              <div className="flex w-fit items-center gap-2">
                <ChatScriptTag
                  item={selectedScript}
                  onRemove={() => setSelectedScript(undefined)}
                />
              </div>
            )}
            <div className="flex justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 overflow-hidden">
                <ChattingMessageEditorActions
                  userId={userId}
                  price={price}
                  giphyId={giphyId}
                  isCountdownIncluded={isChatMessageEditor}
                  fileInputRef={fileInputRef}
                  scheduledDate={scheduledDate}
                  setScheduledDate={setScheduledDate}
                  scheduleDisabled={!!sendAsPriority}
                  onFileChange={handleFileChange}
                  onSelectGif={handleSelectGif}
                  onClickAudio={openAudioDialog}
                  onClickPrice={handlePriceDialogOpen}
                  onClickFormat={handleClickFormat}
                />
              </div>
              <div className="flex">
                {sendAsPriorityAllowed && (
                  <div className="flex h-9 w-32 items-center">
                    <Tooltip
                      content={
                        scheduledDate
                          ? 'Priority message is unavailable due to scheduled messages.'
                          : 'Send this message as Priority'
                      }
                      asChild
                    >
                      <label className="w-full cursor-pointer">
                        <Checkbox
                          checked={sendAsPriority}
                          onCheckedChange={handleSendAsPriorityChange}
                          disabled={!!scheduledDate}
                        />
                        <span className="text-secondary-300 ml-2 text-xs">
                          Send as Priority
                        </span>
                      </label>
                    </Tooltip>
                  </div>
                )}
                <Tooltip
                  content={sendDisabledReason}
                  asChild
                >
                  <Button
                    className="h-9 shrink-0 py-1.5"
                    onClick={handleSubmitButton}
                    disabled={isSendDisabled}
                    isLoading={isLoading}
                    testId="send-button"
                  >
                    Send
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        )}
      </Dropzone>
      <TranslationTutorial />
    </>
  )
}

const ChattingMessageEditorWithProvider = (
  props: Omit<Props, 'message' | 'text'>,
) => {
  return (
    <MessageEditorProvider>
      <ChattingMessageEditorComponent {...props} />
    </MessageEditorProvider>
  )
}

export const ChattingMessageEditor = memo(ChattingMessageEditorWithProvider)

ChattingMessageEditor.displayName = 'ChattingMessageEditor'
